import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Link,
} from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  Reply,
  ReplyAll,
  Forward,
  Star,
  StarOff,
  Paperclip,
  Download,
  MoreVertical,
  Trash2,
  Archive,
  Mail,
  MailOpen,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';

/**
 * EmailDisplay - Single email message display component
 * Shows email content with actions for reply, forward, etc.
 */
const EmailDisplay = ({
  message,
  isExpanded: initialExpanded = true,
  onReply,
  onReplyAll,
  onForward,
  onStar,
  onMarkRead,
  onArchive,
  onDelete,
  onDownloadAttachment,
  showActions = true,
  isLatest = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Format date
  const formattedDate = useMemo(() => {
    if (!message?.receivedOn) return '';
    return format(new Date(message.receivedOn), 'MMM d, yyyy \'at\' h:mm a');
  }, [message?.receivedOn]);

  // Get initials for avatar
  const getInitials = useCallback((name, email) => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '?';
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  // Get recipients display string
  const recipientsDisplay = useMemo(() => {
    if (!message?.to) return '';
    const names = message.to.map(r => r.name || r.email).slice(0, 3);
    const remaining = message.to.length - 3;
    return remaining > 0 
      ? `${names.join(', ')} +${remaining} more`
      : names.join(', ');
  }, [message?.to]);

  // Handle menu
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!message) return null;

  const sender = message.sender || {};
  const hasAttachments = message.attachments?.length > 0 || message.hasAttachments;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        '&:not(:last-child)': { mb: 1 },
      }}
    >
      {/* Header - Always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          p: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Avatar
          sx={{
            bgcolor: message.unread ? 'primary.main' : 'grey.400',
            width: 40,
            height: 40,
            mr: 2,
          }}
        >
          {getInitials(sender.name, sender.email)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: message.unread ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sender.name || sender.email}
            </Typography>
            {hasAttachments && (
              <Paperclip size={14} style={{ color: '#666', flexShrink: 0 }} />
            )}
          </Box>
          
          <Typography variant="caption" color="text.secondary">
            To: {recipientsDisplay}
          </Typography>
          
          {!isExpanded && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {message.bodyPreview}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formattedDate}
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </IconButton>
        </Box>
      </Box>

      {/* Body - Expandable */}
      <Collapse in={isExpanded}>
        <Divider />
        
        {/* Email content */}
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              '& img': { maxWidth: '100%', height: 'auto' },
              '& a': { color: 'primary.main' },
              '& pre': { 
                overflow: 'auto', 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1,
              },
            }}
            dangerouslySetInnerHTML={{ 
              __html: message.decodedBody || message.body || '<p>No content</p>' 
            }}
          />
        </Box>

        {/* Attachments */}
        {message.attachments?.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Attachments ({message.attachments.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {message.attachments.map((attachment, index) => (
                  <Chip
                    key={attachment.attachmentId || index}
                    icon={<Paperclip size={14} />}
                    label={`${attachment.filename} ${formatFileSize(attachment.size)}`}
                    variant="outlined"
                    size="small"
                    onClick={() => onDownloadAttachment?.(message.id, attachment.attachmentId)}
                    deleteIcon={<Download size={14} />}
                    onDelete={() => onDownloadAttachment?.(message.id, attachment.attachmentId)}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {/* Actions */}
        {showActions && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Reply">
                  <IconButton size="small" onClick={() => onReply?.(message)}>
                    <Reply size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reply All">
                  <IconButton size="small" onClick={() => onReplyAll?.(message)}>
                    <ReplyAll size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Forward">
                  <IconButton size="small" onClick={() => onForward?.(message)}>
                    <Forward size={18} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {message.webLink && (
                  <Tooltip title="Open in Outlook">
                    <IconButton 
                      size="small" 
                      component={Link}
                      href={message.webLink}
                      target="_blank"
                    >
                      <ExternalLink size={18} />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton size="small" onClick={handleMenuClick}>
                  <MoreVertical size={18} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => { onMarkRead?.(message.id, message.unread); handleMenuClose(); }}>
                    {message.unread ? (
                      <>
                        <MailOpen size={16} style={{ marginRight: 8 }} />
                        Mark as read
                      </>
                    ) : (
                      <>
                        <Mail size={16} style={{ marginRight: 8 }} />
                        Mark as unread
                      </>
                    )}
                  </MenuItem>
                  <MenuItem onClick={() => { onStar?.(message.id); handleMenuClose(); }}>
                    <Star size={16} style={{ marginRight: 8 }} />
                    Star
                  </MenuItem>
                  <MenuItem onClick={() => { onArchive?.(message.id); handleMenuClose(); }}>
                    <Archive size={16} style={{ marginRight: 8 }} />
                    Archive
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { onDelete?.(message.id); handleMenuClose(); }} sx={{ color: 'error.main' }}>
                    <Trash2 size={16} style={{ marginRight: 8 }} />
                    Delete
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
};

EmailDisplay.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    subject: PropTypes.string,
    sender: PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    to: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    })),
    receivedOn: PropTypes.string,
    unread: PropTypes.bool,
    body: PropTypes.string,
    decodedBody: PropTypes.string,
    bodyPreview: PropTypes.string,
    hasAttachments: PropTypes.bool,
    attachments: PropTypes.array,
    webLink: PropTypes.string,
  }),
  isExpanded: PropTypes.bool,
  onReply: PropTypes.func,
  onReplyAll: PropTypes.func,
  onForward: PropTypes.func,
  onStar: PropTypes.func,
  onMarkRead: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  onDownloadAttachment: PropTypes.func,
  showActions: PropTypes.bool,
  isLatest: PropTypes.bool,
};

export default EmailDisplay;
